import React, { useEffect, useState, useRef } from 'react';
import Modal from 'react-modal';
import { X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOfferPreferenceRequest, RootState } from '@rb-serve-app/core';
import CommanModal from '../../components/CommanModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  img?: string;
  title?: string;
  jsxElement?: any;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  LegalDesc?: string | React.ReactNode;
  onSecondaryBtnClick: () => void;
  onPrimaryBtnClick: () => void;
  creditLimitIncOffer: any;
  balanceTransfer: any;
  offerType?: string;
  isReminderModalOpen?: boolean;
  setIsReminderModalOpen?: (isOpen: boolean) => void;
};

if (typeof document !== 'undefined' && document.getElementById('root')) {
  Modal.setAppElement('#root');
}

const OfferModal: React.FC<Props> = ({
  isOpen,
  onClose,
  img,
  title,
  jsxElement,
  primaryButtonText,
  secondaryButtonText,
  LegalDesc,
  onSecondaryBtnClick,
  onPrimaryBtnClick,
  creditLimitIncOffer,
  balanceTransfer,
  offerType,
  isReminderModalOpen,
  setIsReminderModalOpen,
}) => {
  const [isLegal, setIsLegal] = useState(false);
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { entries, assets } = contentfulData;
  const rb_offer = entries?.rb_offers;
  console.log('rb_offer', rb_offer, assets);
  const { accountId, customerId } = useSelector(
    (state: RootState) => state.user || {}
  );
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  const legalRef = useRef<HTMLDivElement | null>(null);

  const handleLegalSection = () => {
    setIsLegal((prev) => {
      const newState = !prev;
      setTimeout(() => {
        if (newState && legalRef.current) {
          legalRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100); // Delay to ensure DOM has updated
      return newState;
    });
  };

  const dispatch = useDispatch();
  const { offers, loading, targetedOfferPrefSubmit, error } = useSelector(
    (state: RootState) => state.offer || {}
  );
  const currentOfferType = offers?.[0]?.offerType;
  const handleMaybeLaterPopup = () => {
    setIsReminderModalOpen?.(true);
  };

  const getLegalTitle = () => {
    return rb_offer?.requestCard?.descTitle || 'Terms and conditions';
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={false}
      contentLabel="Request a Supplementary Card"
      className="relative w-full xl:w-[35%] lg:w-[70%] md:w-[70%] my-12 bg-white shadow-lg outline-none md:max-h-[80vh] md:h-auto  max-h-[100vh] h-[100vh] overflow-y-auto"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
    >
      <div className="flex flex-col justify-center items-center">
        {/* Image */}
        <img
          src={img} // Replace with actual path
          alt="Smiling couple using laptop"
          className="w-full h-auto mb-5 object-contain"
        />

        {/* Content */}
        {jsxElement}

        {/* Action Buttons */}
        <div className="px-6 pb-6 w-full">
          <div className="flex flex-col sm:flex-row gap-3 mb-4 w-full">
            <button
              aria-label={primaryButtonText}
              className="bg-primary-red text-white px-4 py-2 h-[48px] w-full hover:bg-red-700 focus-visible:ring focus-visible:ring-red-500 text-[16px] font-[600]"
              onClick={onPrimaryBtnClick}
            >
              {primaryButtonText}
            </button>
            <button
              aria-label={secondaryButtonText}
              className="border border-primary-red px-4 py-2 h-[48px] w-full text-primary-red hover:bg-gray-100 focus-visible:ring focus-visible:ring-gray-400 text-[16px] font-[450]"
              onClick={onSecondaryBtnClick}
            >
              {secondaryButtonText}
            </button>
          </div>
          {!creditLimitIncOffer && (
            <div className="py-6">
              <button
                aria-label={
                  balanceTransfer
                    ? rb_offer?.ctaTexts?.mayBeLater
                    : rb_offer?.requestCard?.mayBeLater
                }
                className="text-primary-red underline hover:text-red-800 focus-visible:ring focus-visible:ring-red-800 rounded text-center cursor-pointer w-full text-[16px]"
                onClick={() => handleMaybeLaterPopup()}
              >
                {balanceTransfer
                  ? rb_offer?.ctaTexts?.mayBeLater
                  : rb_offer?.requestCard?.mayBeLater}
              </button>
            </div>
          )}
          {/* Disclaimers */}
          {LegalDesc && (
            <h5
              className="text-xs text-red-600 underline hover:text-red-800 focus-visible:ring focus-visible:ring-red-400 rounded cursor-pointer"
              onClick={handleLegalSection}
              aria-label={rb_offer?.requestCard?.descTitle}
            >
              {isLegal ? `- ${getLegalTitle()}` : `+ ${getLegalTitle()}`}
            </h5>
          )}

          {isLegal && LegalDesc && (
            <div ref={legalRef}>
              <div className="mt-2 text-xs text-black leading-relaxed">
                {typeof LegalDesc === 'string' ? <p>{LegalDesc}</p> : LegalDesc}
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default OfferModal;
